import React, { useState, useEffect, useContext } from 'react'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import queryString from 'query-string'
import {
  Flex,
  Box,
  Label,
  Checkbox,
  Text,
  Link,
  FormField,
  Select,
  theme
} from 'pcln-design-system'
import { usePartner, usePartnerPromo } from '@pcln/whitelabel-components'
import { Calendar } from '@pcln/date-picker'
import pclnExperimentation from '@pcln/experimentation'
import { useFeature } from '@pcln/feature-components'
import CalendarWrapperBox from '../../CalenderWrapperBox'
import DateField from '../../DateField'
import FormContainer from './FormContainer'
import ShadowEffect from '../../ShadowEffect'
import {
  isInfantOnInternationalTravel,
  isChildrenAgeProvided,
  isInfantLimitReachedPackages,
  isRoomCompacityReached,
  searchFormSchema
} from './validation'
import TripTypeRadios from './TripTypeRadios'
import StyledHeading from '../../StyledHeading'
import SearchFormButton from '../../SearchFormButton'
import TypeAhead from '../../TypeAhead'
import {
  formatDate,
  getPackagesDateRange,
  addFormattedDays,
  defaultMaxDate,
  defaultToday,
  getRcMinDate,
  getRcMaxDate,
  getTimeOptions
} from '../../../utils/date-helper'
import { isHotelPlusCar, hasHotel, STAY_DRIVE, STAY_FLY } from './TripTypes'
import { PopoverContainer } from '../../Popover'
import { VP_DISCLAIMER } from '../../TravelerSelection/constants'
import analytics from '../../../utils/analytics'
import TravelerSelection from '../../TravelerSelection/TravelerSelection'
import BootstrapDataContext from '../../../context/BootstrapDataContext'
import ExperimentContext from '../../../context/ExperimentContext'
import {
  fields,
  initialState,
  CALENDAR_POPOVER_SELECTOR,
  getTextString
} from './utils'
import useTabGroup from '../../CustomHooks/useTabGroup'
import useSeti from '../../CustomHooks/useSeti'
import OpenTripsWithPrepopLink from '../OpenTripsWithPrepopLink'
import useContentfulSearchFormHeadlines from '../../CustomHooks/useContentfulSearchFormHeadlines'

const LocationSelector = dynamic(() =>
  import('@pcln/selectors').then(mod => mod.LocationSelector)
)

const InlineBlockBox = styled(Box)`
  display: inline-block;
`

const FlexLabel = styled(Label)`
  display: flex;
  align-items: center;
`

const trackOneWayFlightLinkClick = () => {
  analytics.fireEvent({
    action: 'Search Form Link',
    label: 'I need a one-way flight'
  })
}

const StyledSelect = styled(Select)`
  background: ${theme.colors.white};
  &::-ms-expand {
    display: none;
  }
  cursor: pointer;
`

const TimeField = ({ name, label, ...props }) => (
  <FormField>
    <Label>{label}</Label>
    <StyledSelect {...props} id={name} name={name}>
      {getTimeOptions()}
    </StyledSelect>
  </FormField>
)

TimeField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired
}

const formatOneWayHref = ({
  watchTripType,
  watchStartLocation,
  watchEndLocation,
  watchStartDate,
  watchEndDate,
  watchRoomCount,
  watchAdultCount,
  watchChildrenCount,
  watchChildrenAges,
  watchHotelStartDate,
  watchHotelEndDate,
  watchPartialStay,
  watchIncludeBasicEconomy
}) => {
  const startLocation = watchStartLocation || {}
  const endLocation = watchEndLocation || {}
  const stringifiedQuery = queryString.stringify({
    'flight-code': 'OW',
    'trip-type': watchTripType,
    'origin-item': startLocation.itemName,
    'origin-id': startLocation.id,
    'origin-type': startLocation.type,
    'origin-city': startLocation.cityName,
    'origin-state': startLocation.provinceName,
    'origin-state-code': startLocation.stateCode,
    'origin-country': startLocation.country,
    'origin-country-code': startLocation.countryCode,
    'destination-item': endLocation.itemName,
    'destination-id': endLocation.id,
    'destination-type': endLocation.type,
    'destination-city': endLocation.cityName,
    'destination-state': endLocation.provinceName,
    'destination-state-code': endLocation.stateCode,
    'destination-country': endLocation.country,
    'destination-country-code': endLocation.countryCode,
    'include-basic-economy': watchIncludeBasicEconomy,
    start: watchStartDate,
    end: watchEndDate,
    rooms: watchRoomCount,
    adults: watchAdultCount,
    children: watchChildrenCount,
    ages: JSON.stringify(watchChildrenAges),
    ...(watchPartialStay
      ? {
          'hotel-start': watchHotelStartDate,
          'hotel-end': watchHotelEndDate
        }
      : {})
  })

  return `/trips/?${stringifiedQuery}`
}

const Form = ({ onSubmit, isLandingPage = true }) => {
  const {
    PKG_RECOMMENDED_TRIP_SUMMARY_FLOW = 'DEFAULT',
    PKG_WL_DEFAULT_OFF_BASIC_ECONOMY = 'DEFAULT'
  } = useContext(ExperimentContext)

  const isSingleHotel = usePartner('config.products.hotel.singleLocation')

  const initialSearchValidPromotions = usePartner(
    'config.initialSearchboxValues.nextLandingActivePromo.validPromos'
  )
  const initialSearchValues =
    usePartnerPromo(
      'config.initialSearchboxValues.nextLandingActivePromo.values',
      'config.initialSearchboxValues.nextLanding',
      Array.isArray(initialSearchValidPromotions)
        ? initialSearchValidPromotions
        : []
    ) || {}

  const isPricelineOnly = useFeature('whiteLabel.isPricelineOnly')
  const toggleBasicEconomyEnabled = useFeature(
    'whiteLabel.toggleBasicEconomyEnabled'
  )
  const rentalCarEnabled = useFeature('whiteLabel.packagesRentalCarEnabled')

  const shouldOfferRecommendedTrip =
    isPricelineOnly &&
    PKG_RECOMMENDED_TRIP_SUMMARY_FLOW === 'RECOMMENDED_TRIP_SUMMARY'

  const isUnited = usePartner('partnerCode') === 'untd'

  if (shouldOfferRecommendedTrip) {
    fields.push('isRecommendedTrip')
  }

  useEffect(() => {
    pclnExperimentation.fireImpression(
      'PKG_RECOMMENDED_TRIP_SUMMARY_FLOW',
      {},
      { ga: { dimension: 'dimension89', teamName: 'PACKAGES' } }
    )
    if (isUnited && toggleBasicEconomyEnabled) {
      pclnExperimentation.fireImpression(
        'PKG_WL_DEFAULT_OFF_BASIC_ECONOMY',
        {},
        {}
      )
    }
  }, [])

  const { registerTabElement, focusNextElement } = useTabGroup()
  const { isMobile = false } = useContext(BootstrapDataContext)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [isRcPickupCalendarOpen, setIsRcPickupCalendarOpen] = useState(false)
  const [isRcDropoffCalendarOpen, setIsRcDropoffCalendarOpen] = useState(false)
  const [isHotelCalendarOpen, setIsHotelCalendarOpen] = useState(false)

  const primaryHeadingText = 'Save up to $625 on your '
  const secondaryHeadingText = !isMobile
    ? 'Book together for our best deals'
    : ''

  const partnerSearchForm =
    usePartnerPromo(
      'config.searchFormActivePromo',
      'config.searchForm',
      usePartner('config.searchFormActivePromo.validPromos') || []
    ) || {}

  let partnerPrimaryHeading = partnerSearchForm.primaryHeading

  const partnerSecondaryHeading =
    partnerSearchForm.secondaryHeading || secondaryHeadingText

  const { primaryFontColor, secondaryFontColor } = partnerSearchForm

  const buttonText = partnerSearchForm.buttonText || 'Find Your Trip'

  const partnerCallToAction =
    partnerSearchForm.callToAction ||
    (!rentalCarEnabled &&
      'Book a hotel and flight with free cancellation for flexibility')

  const callToAction =
    partnerCallToAction ||
    'Book a hotel, flight or car with free cancellation for flexibility'

  // TODO: VP_LP_PKG_TYPE_DEFAULT_COVID_SPECIFIC removal
  const VP_LP_PKG_TYPE_DEFAULT_COVID_SPECIFIC = useSeti(
    'VP_LP_PKG_TYPE_DEFAULT_COVID_SPECIFIC'
  )
  const isPkgOffAirportSetiOn = useSeti('PKG_OFF_AIRPORT_RC') !== 'DEFAULT'

  const isOneWayFlightSeti =
    useSeti('PKG_NEW_ONE_WAY_FLIGHT_LINK_VP_LANDING') !== 'DEFAULT'

  const allowedPartnerDestinations =
    partnerSearchForm.customFields?.destinations

  const radioExperimentInitialState = {
    ...initialState,
    ...initialSearchValues,
    includeBasicEconomy:
      !toggleBasicEconomyEnabled ||
      !isUnited ||
      PKG_WL_DEFAULT_OFF_BASIC_ECONOMY !== 'DEFAULT',
    tripType:
      rentalCarEnabled && VP_LP_PKG_TYPE_DEFAULT_COVID_SPECIFIC === 'VARIANT'
        ? STAY_DRIVE
        : STAY_FLY
  }

  const searchForm = useContentfulSearchFormHeadlines('searchForm')

  return (
    <FormContainer
      onSubmit={onSubmit}
      fields={fields}
      initialState={radioExperimentInitialState}
      searchFormSchema={searchFormSchema}
    >
      {({
        setValue,
        errors,
        register,
        watchState: {
          watchTripType,
          watchAdultCount,
          watchRoomCount,
          watchChildrenAges,
          watchChildrenCount,
          watchHotelStartDate,
          watchHotelEndDate,
          watchStartDate,
          watchEndDate,
          watchPartialStay,
          watchStartLocation,
          watchEndLocation,
          watchIncludeBasicEconomy,
          watchPickupCarSomewhere,
          watchRcLocation,
          watchRcPickupDate,
          watchRcDropoffDate,
          watchRcPickupTime,
          watchRcDropoffTime
        }
      }) => {
        const finalPrimaryHeadingText = getTextString(watchTripType)(
          primaryHeadingText
        )
        if (!partnerPrimaryHeading) {
          partnerPrimaryHeading = finalPrimaryHeadingText
        }

        let headline
        let subheadline

        if (isPricelineOnly) {
          const contentfulHeadline = isMobile
            ? searchForm?.mobileHeadline
            : searchForm?.headline
          const contentfulSubheadline = isMobile
            ? searchForm?.mobileSubheadline
            : searchForm?.subheadline
          headline = contentfulHeadline || finalPrimaryHeadingText
          subheadline = contentfulSubheadline || secondaryHeadingText
        }

        return (
          <>
            {isLandingPage && (
              <Flex
                flexDirection="column"
                pt={[2, null, null, 4]}
                pr={[2, null, null, 4]}
                pl={2}
                pb={2}
              >
                <StyledHeading
                  color={primaryFontColor}
                  fontSize={[3, 4, 4, 4, 5]}
                  text={isPricelineOnly ? headline : partnerPrimaryHeading}
                  tag="h1"
                />
                <StyledHeading
                  color={secondaryFontColor || 'text.light'}
                  fontSize={[1, 3, 3, 3, 4]}
                  text={isPricelineOnly ? subheadline : partnerSecondaryHeading}
                  tag="h2"
                />
              </Flex>
            )}
            <Box>
              {rentalCarEnabled && (
                <TripTypeRadios
                  watchTripType={watchTripType}
                  setValue={setValue}
                  register={register}
                />
              )}
              <Flex flexWrap="wrap" mx={-2}>
                {!isHotelPlusCar(watchTripType) && (
                  <Box
                    width={[1, null, isSingleHotel ? 1 : 1 / 2]}
                    px={2}
                    mt={3}
                  >
                    <ShadowEffect enabled forProperty="startLocation">
                      {({ disableShadowState }) => (
                        <TypeAhead
                          searchProduct="packages"
                          label="Where from?"
                          placeholder="Where from?"
                          searchKey="startLocation"
                          setValue={setValue}
                          errors={errors}
                          disableShadowState={disableShadowState}
                          defaultSelectedItem={watchStartLocation}
                          ref={registerTabElement}
                          focusNextElement={focusNextElement}
                        />
                      )}
                    </ShadowEffect>
                  </Box>
                )}
                {!isSingleHotel && (
                  <Box
                    width={[1, null, isHotelPlusCar(watchTripType) ? 1 : 1 / 2]}
                    px={2}
                    mt={3}
                  >
                    {allowedPartnerDestinations ? (
                      <LocationSelector
                        label="Where to?"
                        errorMessage={errors.endLocation?.message}
                        onChange={(_, value) => setValue('endLocation', value)}
                        locations={allowedPartnerDestinations}
                        fieldWidth="100%"
                        fieldHeight="56px"
                        menuWidth="434px"
                        hasShadowEffect
                      />
                    ) : (
                      <ShadowEffect enabled forProperty="endLocation">
                        {({ disableShadowState }) => (
                          <TypeAhead
                            searchProduct="packages"
                            label="Where to?"
                            placeholder="Where to?"
                            searchKey="endLocation"
                            errors={errors}
                            setValue={setValue}
                            disableShadowState={disableShadowState}
                            defaultSelectedItem={watchEndLocation}
                            onItemSelect={item => {
                              // auto-populate RC location if not set yet
                              if (isPkgOffAirportSetiOn && !watchRcLocation) {
                                setValue('rcLocation', item)
                              }
                            }}
                            ref={registerTabElement}
                            focusNextElement={focusNextElement}
                          />
                        )}
                      </ShadowEffect>
                    )}
                  </Box>
                )}
              </Flex>
              {!isOneWayFlightSeti &&
                isPricelineOnly &&
                watchTripType.includes('FLY') && (
                  <InlineBlockBox mt={3}>
                    <Link
                      href={formatOneWayHref({
                        watchTripType,
                        watchStartLocation,
                        watchEndLocation,
                        watchStartDate,
                        watchEndDate,
                        watchRoomCount,
                        watchAdultCount,
                        watchChildrenCount,
                        watchChildrenAges,
                        watchHotelStartDate,
                        watchHotelEndDate,
                        watchPartialStay,
                        watchIncludeBasicEconomy
                      })}
                      target="_blank"
                      onClick={trackOneWayFlightLinkClick}
                    >
                      <Text fontWeight="bold">I need a one-way flight</Text>
                    </Link>
                  </InlineBlockBox>
                )}
              <Flex
                flexWrap="wrap"
                alignItems="flex-start"
                mx={-2}
                mb={-3}
                pb={4}
              >
                <Flex width={[1, null, null, null, 2 / 3]} flexWrap="wrap">
                  <CalendarWrapperBox
                    px={2}
                    mt={3}
                    width={[1, null, 1 / 2]}
                    id="flight-dates"
                  >
                    <ShadowEffect enabled forProperty="startDate">
                      {({ disableShadowState }) => (
                        <PopoverContainer
                          data-calendar
                          open={isCalendarOpen}
                          onDismiss={() => {
                            setIsCalendarOpen(false)
                            disableShadowState()
                          }}
                          domSelectorForVisuallyContainedElement={
                            CALENDAR_POPOVER_SELECTOR
                          }
                        >
                          <DateField
                            name="travelDates"
                            data-testid="packages-date-range"
                            label={
                              isHotelPlusCar(watchTripType)
                                ? 'Check-in   -   Check-out'
                                : 'Departing - Returning'
                            }
                            ref={registerTabElement}
                            value={getPackagesDateRange({
                              startDate: watchStartDate,
                              endDate: watchEndDate
                            })}
                            onFocus={() => {
                              setIsCalendarOpen(true)
                            }}
                            error={(() => {
                              if (errors.startDate) {
                                return errors.startDate.message
                              }
                              if (errors.endDate) {
                                return errors.endDate.message
                              }
                            })()}
                            hideTooltip={false}
                            isMobile={isMobile}
                            aria-expanded={isCalendarOpen ? 'true' : 'false'}
                          />
                          <Calendar
                            monthWidth={300}
                            startDate={watchStartDate}
                            endDate={watchEndDate}
                            onChange={(_, { startDate, endDate }) => {
                              if (startDate && endDate) {
                                setIsCalendarOpen(false)
                                disableShadowState()
                              }
                              setValue('startDate', startDate || '')
                              setValue('endDate', endDate || '')
                              setValue('hotelStartDate', startDate || '')
                              setValue('hotelEndDate', endDate || '')

                              // auto-populate RC dates if not set yet
                              if (isPkgOffAirportSetiOn) {
                                if (!watchRcPickupDate)
                                  setValue('rcPickupDate', startDate)
                                if (!watchRcDropoffDate)
                                  setValue('rcDropoffDate', endDate)
                              }
                            }}
                            useDefaultFooter
                            onDismiss={() => {
                              setIsCalendarOpen(false)
                              disableShadowState()
                            }}
                          />
                        </PopoverContainer>
                      )}
                    </ShadowEffect>
                  </CalendarWrapperBox>
                  <Box px={2} mt={3} width={[1, null, 1 / 2]}>
                    <ShadowEffect
                      enabled
                      forProperty="travelers-selection-readonly-input"
                      alternativeBlurHandling
                    >
                      {({ disableShadowState }) => (
                        <TravelerSelection
                          id="travelers"
                          disclaimer={VP_DISCLAIMER}
                          onDoneButtonClick={disableShadowState}
                          roomCount={watchRoomCount}
                          adultCount={watchAdultCount}
                          childrenAges={watchChildrenAges}
                          childrenCount={watchChildrenCount}
                          showChildAgeSelectors
                          showRoomMenuItem={hasHotel(watchTripType)}
                          showSubLabel={false}
                          setValue={setValue}
                          disableShadowState={disableShadowState}
                          isMobile={isMobile}
                          error={
                            isInfantOnInternationalTravel(
                              watchStartLocation,
                              watchEndLocation,
                              watchChildrenAges
                            ) ||
                            isInfantLimitReachedPackages(
                              watchAdultCount,
                              watchChildrenAges,
                              watchTripType
                            ) ||
                            isChildrenAgeProvided(
                              watchChildrenCount,
                              watchChildrenAges
                            ) ||
                            isRoomCompacityReached(
                              watchAdultCount,
                              watchChildrenCount,
                              watchTripType,
                              watchRoomCount
                            )
                          }
                        />
                      )}
                    </ShadowEffect>
                  </Box>
                  <Box mt={2} ml={1} width={1}>
                    <Flex flexWrap={['wrap', null, 'nowrap']}>
                      {!isSingleHotel &&
                        !isHotelPlusCar(watchTripType) &&
                        hasHotel(watchTripType) && (
                          <FlexLabel nowrap fontSize={0} mr={2}>
                            <Checkbox
                              id="packages-partial-stay"
                              name="partialStay"
                              ref={register}
                            />{' '}
                            I only need a hotel for part of my stay
                          </FlexLabel>
                        )}
                      {toggleBasicEconomyEnabled && (
                        <FlexLabel nowrap fontSize={0}>
                          <Checkbox
                            id="package-include-basic-economy"
                            data-testid="include-basic-economy-toggle"
                            name="includeBasicEconomy"
                            ref={register}
                          />{' '}
                          Show Basic Economy Fares
                        </FlexLabel>
                      )}
                    </Flex>
                    {watchPartialStay && (
                      <Box
                        width={[1, null, 1 / 2]}
                        mt={2}
                        mx={2}
                        pr={[0, null, 3]}
                      >
                        <ShadowEffect enabled forProperty="hotelDates">
                          {({ disableShadowState }) => (
                            <PopoverContainer
                              data-hotel-calendar="true"
                              domSelectorForVisuallyContainedElement={
                                CALENDAR_POPOVER_SELECTOR
                              }
                              onDismiss={() => {
                                setIsHotelCalendarOpen(false)
                                disableShadowState()
                              }}
                              open={isHotelCalendarOpen}
                            >
                              <DateField
                                aria-expanded={
                                  isHotelCalendarOpen ? 'true' : 'false'
                                }
                                error={(() => {
                                  if (errors.hotelStartDate) {
                                    return errors.hotelStartDate.message
                                  }
                                  if (errors.hotelEndDate) {
                                    return errors.hotelEndDate.message
                                  }
                                  if (errors.hotelStartDateInRange) {
                                    return errors.hotelStartDateInRange.message
                                  }
                                  if (errors.hotelEndDateInRange) {
                                    return errors.hotelEndDateInRange.message
                                  }
                                })()}
                                isMobile={isMobile}
                                label="Check-in - Check-out"
                                name="hotelDates"
                                onFocus={() => {
                                  setIsHotelCalendarOpen(true)
                                }}
                                value={getPackagesDateRange({
                                  startDate: watchHotelStartDate,
                                  endDate: watchHotelEndDate
                                })}
                              />
                              <Calendar
                                startDate={watchHotelStartDate}
                                endDate={watchHotelEndDate}
                                maxDate={
                                  watchEndDate
                                    ? addFormattedDays(watchEndDate, 1)
                                    : defaultMaxDate
                                }
                                minDate={watchStartDate || defaultToday}
                                monthWidth={300}
                                onChange={(_, { startDate, endDate }) => {
                                  if (startDate && endDate) {
                                    setIsHotelCalendarOpen(false)
                                    disableShadowState()
                                  }
                                  setValue('hotelStartDate', startDate || '')
                                  setValue('hotelEndDate', endDate || '')
                                }}
                                monthsToShow={
                                  watchStartDate && watchEndDate ? 1 : 2
                                }
                                onDismiss={() => {
                                  setIsHotelCalendarOpen(false)
                                  disableShadowState()
                                }}
                                useDefaultFooter
                              />
                            </PopoverContainer>
                          )}
                        </ShadowEffect>
                      </Box>
                    )}
                    {isOneWayFlightSeti &&
                      isPricelineOnly &&
                      watchTripType.includes('FLY') && (
                        <Box mt={2}>
                          <OpenTripsWithPrepopLink
                            linkText="I need a one-way flight"
                            onClick={trackOneWayFlightLinkClick}
                            values={{
                              adultCount: watchAdultCount,
                              childrenAges: watchChildrenAges,
                              childrenCount: watchChildrenCount,
                              endDate: watchEndDate,
                              endLocationObject: watchEndLocation,
                              flightItineraryTypeCode: 'OW',
                              hotelEndDate: watchHotelEndDate,
                              hotelStartDate: watchHotelStartDate,
                              partialStay: watchPartialStay,
                              roomCount: watchRoomCount,
                              startDate: watchStartDate,
                              startLocationObject: watchStartLocation,
                              tripType: watchTripType
                            }}
                          />
                        </Box>
                      )}
                  </Box>
                </Flex>
                {isPkgOffAirportSetiOn && isHotelPlusCar(watchTripType) && (
                  <Flex flexWrap="wrap" alignItems="flex-start">
                    <Flex mt={2}>
                      <FlexLabel nowrap fontSize={0} ml={1}>
                        <Checkbox
                          id="packages-pickup-car-somewhere"
                          data-testid="packages-pickup-car-somewhere"
                          name="pickupCarSomewhere"
                          ref={register}
                        />{' '}
                        I want to pick up my car somewhere else
                      </FlexLabel>
                    </Flex>
                    {watchPickupCarSomewhere && (
                      <Flex
                        flexWrap="wrap"
                        alignItems="flex-start"
                        width={[1, null, 1]}
                        px={2}
                        mt={2}
                      >
                        <ShadowEffect enabled forProperty="rcLocation">
                          {({ disableShadowState }) => (
                            <TypeAhead
                              searchProduct="packages"
                              label="City, Airport or Address"
                              placeholder="City, Airport or Address"
                              searchKey="rcLocation"
                              data-testid="packages-rc-location"
                              errors={errors}
                              setValue={setValue}
                              disableShadowState={disableShadowState}
                              defaultSelectedItem={watchRcLocation}
                              ref={registerTabElement}
                              focusNextElement={focusNextElement}
                            />
                          )}
                        </ShadowEffect>
                        <Flex mt={2}>
                          <FlexLabel nowrap fontSize={0} ml={-2}>
                            <Checkbox
                              id="packages-pickup-car-include-airport"
                              name="pickupCarIncludeAirport"
                              ref={register}
                            />{' '}
                            Include airport pick-up and drop-off locations
                          </FlexLabel>
                        </Flex>
                        <Flex flexWrap="wrap">
                          <CalendarWrapperBox
                            mt={3}
                            width={[1 / 2, null, 1 / 4]}
                            data-testid="packages-rc-pickup-date"
                            id="rc-pickup-date"
                          >
                            <ShadowEffect enabled forProperty="rcPickupDate">
                              {({ disableShadowState }) => (
                                <PopoverContainer
                                  data-calendar
                                  open={isRcPickupCalendarOpen}
                                  onDismiss={() => {
                                    setIsRcPickupCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                  domSelectorForVisuallyContainedElement={
                                    CALENDAR_POPOVER_SELECTOR
                                  }
                                >
                                  <DateField
                                    name="rcPickupDate"
                                    label="Pick-up"
                                    ref={registerTabElement}
                                    value={formatDate(watchRcPickupDate)}
                                    onFocus={() => {
                                      setIsRcPickupCalendarOpen(true)
                                    }}
                                    error={(() => {
                                      if (errors.rcPickupDate) {
                                        return errors.rcPickupDate.message
                                      }
                                    })()}
                                    hideTooltip={false}
                                    isMobile={isMobile}
                                    aria-expanded={
                                      isRcPickupCalendarOpen ? 'true' : 'false'
                                    }
                                  />
                                  <Calendar
                                    monthsToShow={2}
                                    selectTwoDates={false}
                                    maxDate={getRcMaxDate(watchRcDropoffDate)}
                                    startDate={watchRcPickupDate}
                                    onChange={(_, { startDate }) => {
                                      if (startDate) {
                                        setIsRcPickupCalendarOpen(false)
                                        disableShadowState()
                                      }
                                      setValue('rcPickupDate', startDate || '')
                                    }}
                                    useDefaultFooter
                                    onDismiss={() => {
                                      setIsRcPickupCalendarOpen(false)
                                      disableShadowState()
                                    }}
                                  />
                                </PopoverContainer>
                              )}
                            </ShadowEffect>
                          </CalendarWrapperBox>
                          <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                            <ShadowEffect enabled forProperty="rcPickupTime">
                              <TimeField
                                id="rc-pickup-time"
                                data-testid="packages-rc-pickup-time"
                                name="rcPickupTime"
                                label="Pick-up Time"
                                ref={register}
                                style={{ height: isMobile ? '46px' : '56px' }}
                                value={watchRcPickupTime}
                                onChange={e => {
                                  setValue(
                                    'rcPickupTime',
                                    e.currentTarget?.value
                                  )
                                  e.target.blur()
                                }}
                              />
                            </ShadowEffect>
                          </Box>
                          <CalendarWrapperBox
                            mt={3}
                            width={[1 / 2, null, 1 / 4]}
                            id="rc-dropoff-date"
                            data-testid="packages-rc-dropoff-date"
                          >
                            <ShadowEffect enabled forProperty="rcDropoffDate">
                              {({ disableShadowState }) => (
                                <PopoverContainer
                                  data-calendar
                                  open={isRcDropoffCalendarOpen}
                                  onDismiss={() => {
                                    setIsRcDropoffCalendarOpen(false)
                                    disableShadowState()
                                  }}
                                  domSelectorForVisuallyContainedElement={
                                    CALENDAR_POPOVER_SELECTOR
                                  }
                                >
                                  <DateField
                                    name="rcDropoffDate"
                                    label="Drop-off"
                                    ref={registerTabElement}
                                    value={formatDate(watchRcDropoffDate)}
                                    onFocus={() => {
                                      setIsRcDropoffCalendarOpen(true)
                                    }}
                                    error={(() => {
                                      if (errors.rcDropoffDate) {
                                        return errors.rcDropoffDate.message
                                      }
                                    })()}
                                    hideTooltip={false}
                                    isMobile={isMobile}
                                    aria-expanded={
                                      isRcDropoffCalendarOpen ? 'true' : 'false'
                                    }
                                  />
                                  <Calendar
                                    monthsToShow={2}
                                    selectTwoDates={false}
                                    minDate={getRcMinDate(watchRcPickupDate)}
                                    startDate={watchRcDropoffDate}
                                    onChange={(_, { startDate }) => {
                                      if (startDate) {
                                        setIsRcDropoffCalendarOpen(false)
                                        disableShadowState()
                                      }
                                      setValue('rcDropoffDate', startDate || '')
                                    }}
                                    useDefaultFooter
                                    onDismiss={() => {
                                      setIsRcDropoffCalendarOpen(false)
                                      disableShadowState()
                                    }}
                                  />
                                </PopoverContainer>
                              )}
                            </ShadowEffect>
                          </CalendarWrapperBox>
                          <Box px={2} mt={3} width={[1 / 2, null, 1 / 4]}>
                            <ShadowEffect enabled forProperty="rcDropoffTime">
                              <TimeField
                                id="rc-dropoff-time"
                                name="rcDropoffTime"
                                data-testid="packages-rc-dropoff-time"
                                label="Drop-off Time"
                                ref={register}
                                style={{ height: isMobile ? '46px' : '56px' }}
                                value={watchRcDropoffTime}
                                onChange={e => {
                                  setValue(
                                    'rcDropoffTime',
                                    e.currentTarget?.value
                                  )
                                  e.target.blur()
                                }}
                              />
                            </ShadowEffect>
                          </Box>
                        </Flex>
                      </Flex>
                    )}
                    <Flex flexWrap="wrap" width={[1, null, null, null, 1]}>
                      <Box width={[1, null, 1 / 2]} px={2} mt={3}>
                        <SearchFormButton
                          buttonText={buttonText}
                          handleClick={() => {
                            if (shouldOfferRecommendedTrip) {
                              setValue('isRecommendedTrip', false)
                            }
                          }}
                          isMobile={isMobile}
                          data-testid="find-your-trip-button"
                        />
                      </Box>
                      <Box width={[1, null, 0.4]} px={2} mt={3}>
                        <Text fontSize="12px" mt={2}>
                          {callToAction}
                        </Text>
                      </Box>
                    </Flex>
                  </Flex>
                )}
                {(!isPkgOffAirportSetiOn || !isHotelPlusCar(watchTripType)) && (
                  <Flex
                    flexDirection="column"
                    width={[1, null, null, null, 1 / 3]}
                    px={2}
                    mt={3}
                    ml="auto"
                  >
                    <SearchFormButton
                      buttonText={buttonText}
                      handleClick={() => {
                        if (shouldOfferRecommendedTrip) {
                          setValue('isRecommendedTrip', false)
                        }
                      }}
                      isMobile={isMobile}
                      data-testid="find-your-trip-button"
                    />
                    <Text fontSize="12px" mt={2}>
                      {callToAction}
                    </Text>
                    {/*
                        the Recommended Trip button needs to be hidden so that we can show covid related text copy, when
                        covid is over, we will uncomment out this section
                      {shouldOfferRecommendedTrip && (
                        <Button
                          mt={2}
                          px={0}
                          width={1}
                          variation="link"
                          onClick={() => {
                            setValue('isRecommendedTrip', true)
                          }}
                        >
                          <Text color="primary">See Your Recommended Trip</Text>
                        </Button>
                      )} */}
                  </Flex>
                )}
              </Flex>
            </Box>
          </>
        )
      }}
    </FormContainer>
  )
}

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLandingPage: PropTypes.bool
}

export default Form
